import './CustomStatistics.scss';

import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import moment from 'moment';

import { getAlarmStats, getCriticalAlarmsWithoutNotification, getAlarmHistoricalSummary, getAlarmStatsLoading } from 'src/module/alarm/selector';
import { getMetrics } from 'src/module/facility/selector';
import { getDashboardStats } from 'src/module/device/selector';

import { getNewWorkTicketsLast7Days } from 'src/module/workTicket/selector';
import { getClient, getPluralWorkTicketLabel, getCriticalityLabels } from 'src/module/authentication/selector';
import CustomStatistics from './CustomStatistics';
import { useTranslation } from 'src/i18n';
import { buildDefaultLayout } from 'src/component/DashboardEditor/defaultDashboardLayout';

export default function DashboardStatistics (props) {
  const { t } = useTranslation();

  const {
    defaultLayoutOverride,
    disablePaper,
  } = props;

  const client = useSelector(getClient);
  const alarmStats = useSelector(getAlarmStats);
  const metrics = useSelector(getMetrics);
  const dashboardStats = useSelector(getDashboardStats);
  const criticalAlarmsWithoutNotification = useSelector(getCriticalAlarmsWithoutNotification);
  const newWorkTicketsLast7Days = useSelector(getNewWorkTicketsLast7Days) as any;
  const alarmHistory = useSelector(getAlarmHistoricalSummary) as any;
  const pluralWorkTicketLabel = useSelector(getPluralWorkTicketLabel);
  const criticalityLabels = useSelector(getCriticalityLabels);
  const alarmStatsLoading = useSelector(getAlarmStatsLoading);
  const lastReportSentAt = client?.get('last_report_sent_at');
  const currentTemp = alarmStats?.getIn(['currentSiteTemperature', 'current'], null);

  let circuitsLoto = null;

  const outstandingWorkOrders = (alarmStats?.getIn(['outstandingNotificationsByStatus', 'wo'], 0) || 0) + (alarmStats?.getIn(['outstandingNotificationsByStatus', 'eng_required'], 0) || 0);
  if (alarmStats) {
    circuitsLoto = alarmStats.getIn(['notesByHtc', 'loto']);
  }
  const variables = {
    alarmStats,
    alarmStatsLoading,
    client,
    metrics,
    totalHoursSavedToday: alarmHistory?.size > 0 ? alarmHistory.getIn([alarmHistory.size - 1, 'total_auto_handled'], 'N/A') : 'N/A',
    totalNewNotifications: newWorkTicketsLast7Days?.size || 0,
    criticalAlarmsWithoutNotification,
    circuitsLoto,
    outstandingWorkOrders,
    date: moment().format('YYYY-MM-DD'),
    lastReportSentAt: lastReportSentAt ? moment(lastReportSentAt).format('YYYY-MM-DD') : 'N/A',
    currentSiteTemperature: currentTemp ? `${currentTemp}°C` : 'N/A',
    weekNumber: moment().isoWeek(),
    dashboardStats
  };

  const interpolatedVariables = {
    '%estTotalMaintenanceHoursExplain%': {
      valuePath: 'alarmStats.estTotalMaintenaceHoursExplain',
      default: t('dashboard.totalEstimatedMaintenanceHours')
    },
    '%estBasicTotalMaintenanceHoursExplain%': {
      valuePath: 'alarmStats.estBasicTotalMaintenaceHoursExplain',
      default: t('dashboard.totalEstimatedMaintenanceHours')
    },
    '%mnraLabel%': {
      valuePath: 'client.preferences.notificationLabels.mnra',
      default: t('dashboard.ehtWalkDowns')
    },
    '%engRequiredLabel%': {
      valuePath: 'client.preferences.notificationLabels.eng_required',
      default: t('dashboard.engRequired')
    },
    '%workOrderLabel%': {
      valuePath: 'client.preferences.notificationLabels.wo',
      default: t('dashboard.scheduledRepairs')
    },
  };
  const devicesInAlarmWithoutNotifications = [];
  alarmStats?.get('devicesInAlarmWithoutNotificationByCriticality', [])?.forEach((value,  key) => {
    if (key > 0) {
      devicesInAlarmWithoutNotifications.push({
        type: 'minor',
        title: criticalityLabels?.get(key),
        helpText: t('workTickets.devicesInAlarmWithout', { priority: criticalityLabels?.get(key), label: pluralWorkTicketLabel || t('workTickets.title') }),
        value: `alarmStats.devicesInAlarmWithoutNotificationByCriticality.${key}`,
        defaultValue: 'N/A'
      });
    }
  });

  const dynamicChildren = {
    devicesInAlarmWithoutNotifications
  };

  const defaultLayout = buildDefaultLayout(t);

  return (
    <CustomStatistics
      layoutPreferencePath={props.layoutPreferencePath !== undefined ? props.layoutPreferencePath : 'client.preferences.dashboard.layout'}
      variables={variables}
      interpolatedVariables={interpolatedVariables}
      defaultLayout={defaultLayoutOverride ? defaultLayoutOverride : defaultLayout}
      dynamicChildren={dynamicChildren}
      disablePaper={disablePaper}
    />
  );
}

DashboardStatistics.propTypes = {
  layoutPreferencePath: PropTypes.string,
  defaultLayoutOverride: PropTypes.array,
  disablePaper: PropTypes.bool,
};

DashboardStatistics.defaultProps = {
  defaultLayoutOverride: null,
  disablePaper: false,
};
